import React, { useEffect, useMemo, useState } from "react";

import config from "./config";
import defaultRenderer from "./defaultRenderer";
import { loadRendererPlugin, normalizeRenderer } from "./loader";

/**
 * 渲染阶段错误兜底。
 *
 * 组件里的 try/catch 只能兜住「调用 renderer 创建元素」这一步；元素真正挂载后，
 * 渲染器内部的子组件再抛错会一路冒泡、把整页打白（线上脚本尤其不可控）。
 * 这里用 ErrorBoundary 接住，改用本地默认渲染器重画一次。
 *
 * 兜底内容自己也包一层 boundary：本地实现同样出错时给空，而不是继续冒泡。
 * 换文章（source）/ 换渲染器（renderer）时自动清掉错误态，否则一次失败会让
 * 后续文章一直空白。
 */
function FallbackContent({ render }) {
  return render ? render() : null;
}

class RendererErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { failed: false };
  }

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error) {
    console.error("[MarkdownRenderer] 渲染阶段抛错：", error);
  }

  componentDidUpdate(prevProps) {
    if (!this.state.failed) return;
    if (
      prevProps.source !== this.props.source ||
      prevProps.renderer !== this.props.renderer
    ) {
      this.setState({ failed: false });
    }
  }

  render() {
    if (!this.state.failed) return this.props.children;
    if (!this.props.fallback) return null;
    return (
      <RendererErrorBoundary
        source={this.props.source}
        renderer={this.props.renderer}
        fallback={null}
      >
        <FallbackContent render={this.props.fallback} />
      </RendererErrorBoundary>
    );
  }
}

/**
 * Markdown 渲染器插槽（页面只需要依赖这一个组件）
 *
 *   <MarkdownRenderer markdown={mdString} />
 *
 * 输入：md 字符串；输出：带样式的 React 组件。默认使用本地 ./defaultRenderer.jsx
 * （语雀 md → React，样式内置在 ../yuque_markdown/index.less）。
 *
 * 三种切换方式（页面代码都不用改）：
 *   1. 本地换实现：替换 ./defaultRenderer.jsx（保持 { render(markdown, React) } 契约）
 *   2. 线上脚本：改 ./config.js 的 rendererUrl（或传 rendererUrl prop）
 *   3. 临时指定：传 renderer={{ render(markdown, React) { ... } }}，优先级最高
 *
 * 线上脚本契约、缓存、超时、地址限制见 ./loader.js。
 * 线上脚本加载失败时默认回退本地渲染器；可用 fallbackOnError={false} 关掉回退。
 * 渲染器在挂载 / 更新阶段抛错时由内部 ErrorBoundary 接住，同样回退本地渲染器。
 */
export default function MarkdownRenderer({
  markdown,
  renderer,
  rendererUrl,
  rendererId,
  fallbackOnError = true,
  loadingPlaceholder = null,
  className,
  ...rest
}) {
  const source = typeof markdown === "string" ? markdown : "";
  const url = rendererUrl !== undefined ? rendererUrl : config.rendererUrl;
  const id = rendererId !== undefined ? rendererId : config.rendererId;

  /** 调用方直接传进来的渲染器（函数或 { render } 对象） */
  const inlineRenderer = useMemo(() => normalizeRenderer(renderer), [renderer]);

  const [remoteRenderer, setRemoteRenderer] = useState(null);
  const [loadFailed, setLoadFailed] = useState(false);

  useEffect(() => {
    if (inlineRenderer || !url) return undefined;
    if (typeof window === "undefined") return undefined;

    let cancelled = false;
    setLoadFailed(false);
    setRemoteRenderer(null);

    loadRendererPlugin(url, id)
      .then((plugin) => {
        if (!cancelled) setRemoteRenderer(plugin);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error(
          "[MarkdownRenderer] 线上渲染器不可用，已回退本地默认渲染器：",
          err
        );
        setLoadFailed(true);
      });

    return () => {
      cancelled = true;
    };
  }, [inlineRenderer, url, id]);

  const canUseDefault = !url || (loadFailed && fallbackOnError);
  const activeRenderer =
    inlineRenderer || remoteRenderer || (canUseDefault ? defaultRenderer : null);

  const content = useMemo(() => {
    if (!activeRenderer || !source.trim()) return null;
    try {
      return activeRenderer.render(source, React) ?? null;
    } catch (err) {
      console.error("[MarkdownRenderer] 渲染器执行失败：", err);
      /* 线上渲染器在渲染阶段抛错时，也回退到本地默认实现 */
      if (fallbackOnError && activeRenderer !== defaultRenderer) {
        try {
          return defaultRenderer.render(source, React) ?? null;
        } catch (fallbackErr) {
          console.error("[MarkdownRenderer] 默认渲染器也失败了：", fallbackErr);
        }
      }
      return null;
    }
  }, [activeRenderer, source, fallbackOnError]);

  if (!source.trim()) return null;

  /* 线上脚本加载中：先给占位；失败且不允许回退时返回 null */
  if (!activeRenderer) {
    return loadFailed ? null : loadingPlaceholder || null;
  }

  const classes = className ? "md-renderer " + className : "md-renderer";

  /** 挂载阶段兜底：只在真的抛错后才执行，不额外解析一遍正文 */
  const renderFallback = () => {
    if (!fallbackOnError || activeRenderer === defaultRenderer) return null;
    try {
      return defaultRenderer.render(source, React) ?? null;
    } catch (err) {
      console.error("[MarkdownRenderer] 默认渲染器也失败了：", err);
      return null;
    }
  };

  return (
    <div className={classes} {...rest}>
      <RendererErrorBoundary
        source={source}
        renderer={activeRenderer}
        fallback={renderFallback}
      >
        {content}
      </RendererErrorBoundary>
    </div>
  );
}

export { defaultRenderer };
